// src/components/publishing/destination-field-editors.tsx
import React from "react";
import {
  HashnodeContent, WordPressContent, LinkedInContent,
} from "@/app/services/publishing/publishing-api";

const inputSt: React.CSSProperties = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1px solid var(--border)", backgroundColor: "var(--background)",
  fontSize: 13, color: "var(--foreground)",
  outline: "none", boxSizing: "border-box",
  fontFamily: "inherit",
};

function FieldLabel({ children, count }: { children: React.ReactNode; count?: React.ReactNode }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: 5,
    }}>
      <span style={{
        fontSize: 11, fontWeight: 700, color: "var(--muted-foreground)",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {children}
      </span>
      {count !== undefined && (
        <span style={{ fontSize: 10, color: "var(--muted-foreground)" }}>
          {count}
        </span>
      )}
    </div>
  );
}

// ─── Hashnode editor ─────────────────────────────────────────────────────────

export function HashnodeEditor({
  content, onChange,
}: {
  content: HashnodeContent;
  onChange: (patch: Partial<HashnodeContent>) => void;
}) {
  const title = content.title || "";
  const contentMd = content.content_markdown || "";
  const tags = (content.tags || []).join(", ");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <FieldLabel count={`${title.length}/250`}>Title</FieldLabel>
        <input
          value={title}
          onChange={e => onChange({ title: e.target.value })}
          placeholder="Your article title"
          style={inputSt}
        />
      </div>

      <div>
        <FieldLabel count={`${contentMd.length} chars · ~${Math.round(contentMd.split(/\s+/).filter(Boolean).length)} words`}>
          Content (Markdown)
        </FieldLabel>
        <textarea
          value={contentMd}
          onChange={e => onChange({ content_markdown: e.target.value })}
          placeholder="# Heading&#10;&#10;Paste or type your markdown article here…"
          rows={16}
          style={{
            ...inputSt,
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 12, lineHeight: 1.6, resize: "vertical",
          }}
        />
        <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
          Hashnode renders GitHub-flavored Markdown — headings, lists, code blocks, tables.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <FieldLabel>Tags (comma-separated, up to 5)</FieldLabel>
          <input
            value={tags}
            onChange={e => {
              const list = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
              onChange({ tags: list });
            }}
            placeholder="ai, saas, branding"
            style={inputSt}
          />
        </div>
        <div>
          <FieldLabel>Slug (optional)</FieldLabel>
          <input
            value={content.slug || ""}
            onChange={e => onChange({ slug: e.target.value })}
            placeholder="auto-generated from title"
            style={{ ...inputSt, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── WordPress editor ────────────────────────────────────────────────────────

export function WordPressEditor({
  content, onChange,
}: {
  content: WordPressContent;
  onChange: (patch: Partial<WordPressContent>) => void;
}) {
  const title = content.title || "";
  const html = content.content_html || "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <FieldLabel count={`${title.length}/200`}>Title</FieldLabel>
        <input
          value={title}
          onChange={e => onChange({ title: e.target.value })}
          placeholder="Your post title"
          style={inputSt}
        />
      </div>

      <div>
        <FieldLabel count={`${html.length} chars · ~${Math.round(html.split(/\s+/).filter(Boolean).length)} words`}>
          Content (HTML)
        </FieldLabel>
        <textarea
          value={html}
          onChange={e => onChange({ content_html: e.target.value })}
          placeholder="<h2>Heading</h2>&#10;<p>Paste or type HTML here…</p>"
          rows={16}
          style={{
            ...inputSt,
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 12, lineHeight: 1.6, resize: "vertical",
          }}
        />
        <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
          Full HTML allowed. No &lt;html&gt; or &lt;body&gt; wrappers.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
        <div>
          <FieldLabel count={`${(content.excerpt || "").length}/300`}>Excerpt (optional)</FieldLabel>
          <textarea
            value={content.excerpt || ""}
            onChange={e => onChange({ excerpt: e.target.value })}
            placeholder="Short summary for list views and SEO"
            rows={3}
            style={{ ...inputSt, resize: "vertical" }}
          />
        </div>
        <div>
          <FieldLabel>Publish as</FieldLabel>
          <select
            value={content.status || "draft"}
            onChange={e => onChange({ status: e.target.value as any })}
            style={inputSt}
          >
            <option value="draft">Draft</option>
            <option value="publish">Published</option>
            <option value="pending">Pending review</option>
            <option value="private">Private</option>
          </select>
        </div>
      </div>
    </div>
  );
}

// ─── LinkedIn editor ─────────────────────────────────────────────────────────

export function LinkedInEditor({
  content, onChange,
}: {
  content: LinkedInContent;
  onChange: (patch: Partial<LinkedInContent>) => void;
}) {
  const text = content.text || "";
  const overSoft = text.length > 1300;
  const overHard = text.length > 3000;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <FieldLabel count={
          <span style={{ color: overHard ? "#EF4444" : overSoft ? "#D97706" : "var(--muted-foreground)" }}>
            {text.length}/3000 {overSoft && !overHard && "· truncates in feed"}
          </span>
        }>
          Post text
        </FieldLabel>
        <textarea
          value={text}
          onChange={e => onChange({ text: e.target.value })}
          placeholder="Write your LinkedIn post. Put the hook on the first line — everything after the first ~210 chars is hidden behind 'see more'."
          rows={12}
          style={{ ...inputSt, lineHeight: 1.6, resize: "vertical" }}
        />
        <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
          The first ~210 chars show in the feed before "…see more". Lead with a hook.
        </p>
      </div>

      <div>
        <FieldLabel>Visibility</FieldLabel>
        <select
          value={content.visibility || "PUBLIC"}
          onChange={e => onChange({ visibility: e.target.value as any })}
          style={inputSt}
        >
          <option value="PUBLIC">Public (anyone on LinkedIn)</option>
          <option value="CONNECTIONS">Connections only</option>
        </select>
        <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
          Company Page posts are always Public.
        </p>
      </div>
    </div>
  );
}